import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import Dropzone from 'react-dropzone'
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react'
import { Button, Dialog, DialogActions, DialogContent, IconButton, Typography, Tooltip, Switch, LinearProgress } from '@material-ui/core'
import { Send } from '@material-ui/icons'
import { Close } from '@material-ui/icons'
import { Icon, Space, theme, Text } from 'ui'
import useStore from 'store'
import { MessageDialogMode } from '../../utils/types'
import { userIsSuperAdmin, userIsDirectorOrOperator } from '../../utils/roles'
import { useTranslation } from 'react-i18next'
import { GeneralInput } from 'ui/Input/Input'
import MessagesDateTimeAccordion from './MessagesDateTimeAccordion'
import SurveyList from 'components/Messages/Survey/Survey'
import PdfPreview from './PdfPreview'
import MessagesHeader from 'components/Messages/MessagesHeader'
import WarningDialog from 'components/WarningDialog'
import { GroupType } from 'utils/types'
import { _GSPS2PDF } from "utils/pdf-compress/worker-init.js"
import { processVideo } from "utils/video-compress/worker-init.js"

// import style manually
import 'react-markdown-editor-lite/lib/index.css';
import RichTextEditor from './RichTextEditor'
import VideoViewer from './VideoViewer'
import videoDefaultThumbnail from 'assets/video_placeholder01.svg'
import imageCompression from 'browser-image-compression'

const IMAGE_MAX_HEIGHT_AND_WIDTH = 800
const MIN_SIZE_TO_COMPRESS = 1200000
const MAX_FILE_SIZE_MB = 3
               
const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(2),
    },
  },
}));

const LinearIndeterminate = ({ value }: any) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
       {value == -200 ? <LinearProgress variant='indeterminate'/> : <LinearProgress  value={value} variant='determinate'/>}
    </div>
  );
}

const MAX_PDF_SIZE = 5300000
const MAX_VIDEO_SIZE = 50000000

export enum DeliveryType {
  ImmediateWeek = 'immediateWeek',
  Immediate2Weeks = 'immediate2Weeks',
  ImmediateMonth = 'immediateMonth',
  ImmediateYear = 'immediateYear',
  ImmediateNoExpiry = 'immediateNoExpiry',
  CustomDateTime = 'customDateTime'
}

interface Props { }

const StyledDialog = styled(Dialog)`
  align-self: center;
  justify-self: center;
  .MuiDialog-paperScrollPaper {
  /* min-height: 70vh;
  width:1000px; */
  max-height: 85vh;
  border:1px solid white;
  }
`
const DropArea = styled.div`
flex:1;
  padding: 1rem;
  border-radius: 4px;
  color: ${theme.color.lightBlueText};
  background: ${p => p.color + '1A'};
  border: 1px dashed ${theme.color.lightBlueText};
  display: flex;
  align-items: center;
  justify-content: center;
  p {
    text-align: center;
  }
`

const DropzoneIcon = styled(Icon)`
  margin: 1rem 0.4rem;
`

const SendIcon = styled(Send)`
  body[dir='rtl'] & {
    transform: scaleX(-1)
  }
`

const ConfirmButton = styled(Button)`
  &.MuiButtonBase-root {
    font-size: ${theme.fontSize.large};
    font-weight: 500;
    min-width: 140px;
    height: 50px;
    margin: 0.5rem;
    background: ${theme.color.confirmButton};
    &:hover {
      background: ${theme.color.darkBlue};
    }
    color: ${theme.color.white};
    &.Mui-disabled {
      opacity: 0.5;
    }
  }
`
const DropAreaContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
`
const DropAreaIconsContainer = styled.div`
  display: flex;
  flex-direction: row;
`
const Container = styled.div`
 display:flex;
 justify-content:center;
 align-items:center;
 position:relative;
`
const StyledButton = styled(IconButton)`
position: absolute;
    /* top: 30px; */
    left: 10px;
    top:0;
    z-index: 100;
    color:white;
`
const StyledImage = styled.img`
  height:100%;
  width:100%;
  object-fit:contain;
`
const HoverPointer = styled.div`

:hover {
  cursor: pointer;
}
`
const ContainerDialog = styled.div<{ color: string }>`
  max-height: 650px;
  background-color:${p => p.color + '1A'};
  overflow: hidden;
  overflow-y:auto;
`
const defaultVideoImage = async () => {
  const response = await fetch(videoDefaultThumbnail);
  const data = await response.blob();
  const metadata = {
    type: 'image/svg+xml'
  };

  return new File([data], "default.svg", metadata);
}

const isWebAssemblySupported = () => {
  try {
      if (typeof WebAssembly === "object"
          && typeof WebAssembly.instantiate === "function") {
          const module = new WebAssembly.Module(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00));
          if (module instanceof WebAssembly.Module)
              return new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
      }
  } catch (e) {
    return false;
  }

  return false;
};

const imageFromFrame = (videoFile: any, frameTimeInSeconds = 1) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const video = document.createElement('video')
    const source = document.createElement('source')
    const context = canvas.getContext('2d')
    const urlRef = URL.createObjectURL(videoFile)

    video.style.display = 'none'
    canvas.style.display = 'none'

    source.setAttribute('src', urlRef)
    video.setAttribute('crossorigin', 'anonymous')

    video.appendChild(source)
    document.body.appendChild(canvas)
    document.body.appendChild(video)

    if (!context) {
      console.log(`Couldn't retrieve context 2d`)
      resolve(null)
      video.remove()
      canvas.remove()  
      return
    }

    video.currentTime = frameTimeInSeconds
    video.load()

    video.addEventListener('loadedmetadata', function () {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
    })

    video.addEventListener('loadeddata', function () {
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null)
          video.remove()
          canvas.remove()  
          return
        }

        resolve(new File([blob], 'thumbnail', {type: 'image/png'}))
      
        URL.revokeObjectURL(urlRef)
        video.remove()
        canvas.remove()
      }, 'image/png')
    })
  })
}

const RenderSurveyMessage = ({ setNumOfSurveyStart }: { setNumOfSurveyStart: (item: number) => void }) => {
  return <SurveyList
    setNumOfSurveyStart={setNumOfSurveyStart}
  />
}


const RenderRegularMessage = observer(({ handleUpload, openMessageWarning, videoLoading }: { handleUpload: any, openMessageWarning: any, videoLoading: boolean }) => {
  const { t } = useTranslation(['MessageDialog'])
  const { messages, groups, newMessage, modulesStore } = useStore()
  const [videoCompressionProgress, setVideoProgressCompression] = useState(0)
  const file = newMessage.files
  const groupColor = groups.currentGroup?.group.description?.color || theme.color.black
  const updateMessage = newMessage?.currentUserMessage?.message.id != 0
  const videoModule = modulesStore.communityFeatures.find(el => el.featureName === 'Video')
  return <Container>
               
    {file === undefined ? <Dropzone
      onDropRejected={() => openMessageWarning(t`notFotmat`)}
      onDrop={(file) => handleUpload(file, setVideoProgressCompression)}
      multiple={false}
      accept={['application/pdf', 'image/png', 'image/jpg', 'image/jpeg'].concat(videoModule && videoModule.enabled ? ['video/mp4','video/mpeg','video/webm'] : [])}
    >
      {({ getRootProps, getInputProps }) => (
       <Tooltip title={videoModule && videoModule.enabled ? t`fileOptionsWithVideo` : t`fileOptions`}><DropArea color={groupColor} {...getRootProps()}>
          <input {...getInputProps()} />
          <DropAreaContent>
            <DropAreaIconsContainer>
              {videoModule && videoModule.enabled && <DropzoneIcon namespace='mekome' name='video' size={32} />}
              <DropzoneIcon namespace='mekome' name='galleryRoundGreen' size={34} />
              <DropzoneIcon namespace='mekome' name='documentRoundBlue' size={34} />
            </DropAreaIconsContainer>
            <Text textcolor={theme.color.black + 'A1'}>{t`dragFiles`}</Text>
            {videoLoading && <>
              <LinearIndeterminate value={videoCompressionProgress}/>
              <div style={{ color: '#1a90ff' }}>{file?.type === 'VIDEO' ? t`app:preparing_video` : t`app:preparing_file`}</div>
            </>}
          </DropAreaContent>
        </DropArea>
       
      </Tooltip> 
      )}
    </Dropzone>:
      <div style={{ flex: 1 }}>
        {<StyledButton
          onClick={() => newMessage.clearFiles()}>
          <Close style={{ color: 'black' }} />
        </StyledButton>}
        <HoverPointer>
          {(file?.type === 'PDF') && 
            <PdfPreview height={900} file={file?.url} />}
          {(file?.type === 'VIDEO') && 
            <VideoViewer 
              widthClass=".rc-md-editor" 
              fileUrl={file?.local ? window.URL.createObjectURL(file?.url) : file?.url} 
              fileType={file?.local ? file?.url?.type : null}
              thumbnail={newMessage.videoThumbhail ? newMessage.videoThumbhail.local ? URL.createObjectURL(newMessage.videoThumbhail.url) : newMessage.videoThumbhail.url : null }
            />}
          {(file?.type === 'IMAGE') &&   
            <StyledImage
              onClick={() => messages.openViewerModal('image', file?.local ? URL.createObjectURL(file.url) : file?.url)}
              src={ file?.local ? URL.createObjectURL(file.url) : file?.url}
            />
          }
        </HoverPointer>
      </div>
    }
  </Container>
});

async function loadPDFData(response: any, filename: any) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", response);
    xhr.responseType = "arraybuffer";
    xhr.onload = function () {
      window.URL.revokeObjectURL(response);
      const blob = new Blob([xhr.response], {type: "application/pdf"});
      resolve(new File([blob], filename, {
        type: "application/pdf"
      }));
    };
    xhr.send();
  });
}


async function compressPdf(pdf: any) {
  if (MIN_SIZE_TO_COMPRESS > pdf.size) {
    return pdf;
  }
  
  const dataObject = { psDataURL: URL.createObjectURL(pdf) };
  const element = await _GSPS2PDF(dataObject)

  return loadPDFData(element, pdf.name)
}

async function compressImage(file: any, setVideoProgressCompression: any) {
  if (MIN_SIZE_TO_COMPRESS > file.size) {
    return file[0];
  }

  console.log("Start compress action");

  const blob = await imageCompression(file[0], { 
    maxSizeMB: MAX_FILE_SIZE_MB, 
    maxWidthOrHeight: IMAGE_MAX_HEIGHT_AND_WIDTH,
    onProgress: (progress) => {
      setVideoProgressCompression(progress)
    } 
  })

  return blob
}

async function compressVideo(inputFile: any, setVideoProgressCompression: any) {
  if (MIN_SIZE_TO_COMPRESS > inputFile.size) {
    return inputFile;
  }

  if (!isWebAssemblySupported()) {
    console.log("WebAssembly is not supported");
    return inputFile
  }

  console.log("Start compress action");

  const dataObject = { 
    dataURL: URL.createObjectURL(inputFile), 
    type: inputFile.type 
  };

  const url = await processVideo(dataObject, setVideoProgressCompression)

  const res = await fetch(url);
  const blob = await res.blob();

  window.URL.revokeObjectURL(url);

  return blob as any
}

const MessageDialog: FC<Props> = () => {
  const { t, i18n } = useTranslation(['MessageDialog'])
  const { t: t2 } = useTranslation('app')
  const { messages, groups, newMessage, orgs, modulesStore } = useStore()
  const [input, setInput] = useState('')
  const updateMessage = newMessage?.currentUserMessage?.message.id != 0
  const [videoLoading, setVideoLoading] = useState(false)
  const [openWarning, setOpenWarning] = useState(false)
  const [warningMessage, setWarningMessage] = useState('')
  const groupColor = groups.currentGroup?.group.description?.color || theme.color.black
  const supportCallType = groups.currentGroup?.group?.type === GroupType.SUPPORT
  const videoModule = modulesStore.communityFeatures.find(el => el.featureName === 'Video')

  useEffect(() => {
    setInput(newMessage.messageText || '')
  }, [newMessage?.currentUserMessage]) 

  const handleUpload = async (file: File[], setVideoProgressCompression: any) => {
    if (!file || file.length === 0) {
      return
    }

    const typePdf = ['application/pdf']
    const typeVideo = ['video/mp4']
    const typeImage = ['image/jpeg', 'image/png']

    let validTypes = [...typeImage, ...typePdf]
    
    if (videoModule && videoModule.enabled) {
      validTypes = [...typeVideo, ...validTypes]
    }

    if (!validTypes.includes(file[0]?.type)) {
      openMessageWarning(videoModule && videoModule.enabled ? t`notFotmatWithVideo` : t`notFotmat`)
      return
    }

    if (file[0]?.size > MAX_PDF_SIZE && file[0]?.type === 'application/pdf') {
      openMessageWarning(t`fileTooBig`)
      return
    }

    if (file[0]?.size > MAX_VIDEO_SIZE && typeVideo.includes(file[0].type)) {
      openMessageWarning(t`app:video_max_size`)
      return
    }

    if (typePdf.includes(file[0].type)) {
      setVideoProgressCompression(-200)
      setVideoLoading(true)
      const compressedPdf = await compressPdf(file[0])
      newMessage.setFiles(compressedPdf, 'PDF', true)
      setVideoLoading(false)
      setVideoProgressCompression(0)
    } else if(typeImage.includes(file[0].type)) {
      setVideoLoading(true)
      setVideoProgressCompression(0)
      const compressedImage = await compressImage(file, setVideoProgressCompression)
      newMessage.setFiles(compressedImage, 'IMAGE', true)
      setVideoLoading(false)

    } else {
      newMessage.videoThumbhail = undefined

      try {
        setVideoLoading(true)
        setVideoProgressCompression(0)
        const thumbnail = await imageFromFrame(file[0])
        const comressedFile = await compressVideo(file[0], setVideoProgressCompression)
        setVideoLoading(false)

        if (thumbnail === null) {
          const defaultThumbnail = await defaultVideoImage()
          newMessage.setVideoThumbnail(defaultThumbnail, `IMAGE`, true)
          newMessage.setFiles(comressedFile, `VIDEO`, true)
        } else {
          newMessage.setVideoThumbnail(thumbnail, `IMAGE`, true)
          newMessage.setFiles(comressedFile, `VIDEO`, true)
        }  
      } catch(e) {
        console.error(e)
        const defaultThumbnail = await defaultVideoImage()
        newMessage.setVideoThumbnail(defaultThumbnail, `IMAGE`, true)
        newMessage.setFiles(file[0], `VIDEO`, true)
      }
    }
  }

  const handleOk = () => {
    messages.closeNewMessageDialog()
    newMessage.createMessage()
  }

  const onClose = () => {
    newMessage.clearAll()
    messages.closeNewMessageDialog()
  }

  const [numOfSurveyStart, setNumOfSurveyStart] = useState(0)

  const isDisabled = () => {
    if (!input.length && !newMessage.files && !newMessage.surveyAnswers) {
      return true
    }
    if (newMessage.surveyAnswers && newMessage.surveyAnswers.length === 0) {
      return true
    }
    if (updateMessage && newMessage.surveyAnswers && newMessage?.surveyAnswers?.length < numOfSurveyStart) {
      return true
    }

    if (updateMessage && !newMessage.files && newMessage?.currentUserMessage?.message.msg_strategy === 'MEDIA' ) {
      return true
    }

    if (videoLoading) {
      return true
    }
  }

  const openMessageWarning = (text: string) => {
    setOpenWarning(true)
    setWarningMessage(text)
  }

  const showLocationRequestCheckbox = () => {
    return ((modulesStore.isEnabledLocationFeature && userIsDirectorOrOperator()) || userIsSuperAdmin()) && groups.currentGroup?.group.type !== GroupType.SUPPORT
  }

  function handleEditorChange({ html, text }: { html: any, text: string }) {
    if (text !== undefined) {
      newMessage.updateMessageText(text)
    }
    setInput(text)
  }

  return (
    <StyledDialog
      // scroll='paper'
      open={messages.isModalOpen}
      // onClose={onClose}
      // onBackdropClick={()=>{}}
      fullWidth
    // maxWidth='md'

    >
      <MessagesHeader
        textVarient={'black'}
        backColor={groupColor + '1A'}
        secondText={updateMessage ? t`MessageDialog:messagesEditMessage` : supportCallType ? t`serviceCall:new_service_call` :t`messagesNewMessage`}
        mainText={supportCallType ? `${(orgs.currentOrg?.name || '')} ${t`serviceCall:services`} | ${groups?.currentGroup?.group?.name} `:groups?.currentGroup?.group?.name}
        onClose={onClose}
      />

      <ContainerDialog color={groupColor}>
        <DialogContent>
          {showLocationRequestCheckbox() && 
          <>
          <Switch
            color="primary"
            value={newMessage.locationRequest}
            onChange={(e) => {
              newMessage.updateLocationRequestToggle(e.target.checked)
            }}
            checked={newMessage.locationRequest}
          />{t`app:request_location_on_read`}  
          </>}
          <Space />
          {!supportCallType && <MessagesDateTimeAccordion />}
          <Space />
          {/* {!deliveryTimeExpanded && (
          <Appear> */}
          <RichTextEditor
            value={input}
            onChange={handleEditorChange}
            placeholder={messages.messageDialogMode === MessageDialogMode.Survey ? t('Survey:question') : t`messageBody`}
          />
          <Space height={1} />
          {!newMessage.surveyAnswers && <RenderRegularMessage
            videoLoading={videoLoading}
            handleUpload={handleUpload}
            openMessageWarning={openMessageWarning}
          />}
          {newMessage.surveyAnswers && <RenderSurveyMessage
            setNumOfSurveyStart={setNumOfSurveyStart} />}
          {/* </Appear>
        )} */}
        </DialogContent>
      </ContainerDialog>
      <DialogActions style={{ backgroundColor: groupColor + '1A' }}>
        <ConfirmButton disabled={isDisabled()} endIcon={<SendIcon />} onClick={handleOk}>{t2(updateMessage ? 'save' : 'send')}</ConfirmButton>
      </DialogActions>
      <WarningDialog
        isOpen={openWarning}
        okAction={() => setOpenWarning(false)}
        cancelAction={() => setOpenWarning(false)}
        singleText={warningMessage}
      />
    </StyledDialog>
  )
}

export default observer(MessageDialog)
